// Google Picker API integration
declare global {
  interface Window {
    gapi: any;
    google: any;
    googleAccountId: any;
  }
}

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  webViewLink?: string;
  thumbnailLink?: string;
}

export interface GooglePickerConfig {
  clientId: string;
  apiKey: string;
  scope: string;
}

class GooglePickerService {
  private isInitialized = false;
  private pickerApiLoaded = false;
  private oauthToken: string | null = null;
  private config: GooglePickerConfig | null = null;
  private tokenClient: any = null;

  async initialize(config: GooglePickerConfig): Promise<void> {
    this.config = config;
    
    try {
      // Wait for Google API to be available
      await this.waitForGoogleAPI();
      
      // Initialize with the new Google Identity Services
      await this.initializeGoogleIdentityServices();
      
      // Load the Picker API
      await this.loadPickerApi();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Google Picker:', error);
      throw error;
    }
  }

  private waitForGoogleAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      const checkAPI = () => {
        attempts++;
        if (window.google) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Google API failed to load after 5 seconds'));
        } else {
          setTimeout(checkAPI, 100);
        }
      };
      
      checkAPI();
    });
  }

  private initializeGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        reject(new Error('Google API not loaded'));
        return;
      }

      try {
        // Initialize the Google Identity Services
        this.tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: this.config!.clientId,
          scope: this.config!.scope,
          callback: (response: any) => {
            if (response.error) {
              reject(new Error(`OAuth error: ${response.error}`));
            } else {
              this.oauthToken = response.access_token;
              resolve();
            }
          },
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  private loadPickerApi(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.picker) {
        this.pickerApiLoaded = true;
        resolve();
      } else {
        // Load the picker API
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => {
          window.gapi.load('picker', () => {
            this.pickerApiLoaded = true;
            resolve();
          });
        };
        script.onerror = reject;
        document.head.appendChild(script);
      }
    });
  }

  async authenticate(): Promise<string> {
    if (!this.isInitialized) {
      throw new Error('Google Picker not initialized');
    }

    if (this.oauthToken) {
      return this.oauthToken;
    }

    try {
      // Request token using the new Google Identity Services
      if (this.tokenClient) {
        return new Promise<string>((resolve, reject) => {
          // Set up a one-time callback for this authentication request
          const originalCallback = this.tokenClient.callback;
          this.tokenClient.callback = (response: any) => {
            // Restore the original callback
            this.tokenClient.callback = originalCallback;
            
            if (response.error) {
              reject(new Error(`OAuth error: ${response.error}`));
            } else {
              this.oauthToken = response.access_token;
              if (this.oauthToken) {
                resolve(this.oauthToken);
              } else {
                reject(new Error('Failed to get access token'));
              }
            }
          };
          
          this.tokenClient.requestAccessToken();
        });
      } else {
        throw new Error('Token client not initialized');
      }
    } catch (error) {
      console.error('Google authentication failed:', error);
      throw error;
    }
  }

  async showPicker(
    onFileSelect: (files: GoogleDriveFile[]) => void,
    onCancel?: () => void
  ): Promise<void> {
    if (!this.isInitialized || !this.pickerApiLoaded) {
      throw new Error('Google Picker not initialized');
    }

    if (!this.oauthToken) {
      await this.authenticate();
    }

    const picker = new window.google.picker.PickerBuilder()
      .addView(new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes('application/pdf,application/vnd.google-apps.document,application/vnd.google-apps.spreadsheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      )
      .setOAuthToken(this.oauthToken!)
      .setDeveloperKey(this.config!.apiKey)
      .setCallback((data: any) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const files: GoogleDriveFile[] = data.docs.map((doc: any) => ({
            id: doc.id,
            name: doc.name,
            mimeType: doc.mimeType,
            size: doc.sizeBytes,
            webViewLink: doc.url,
            thumbnailLink: doc.iconUrl,
          }));
          onFileSelect(files);
        } else if (data.action === window.google.picker.Action.CANCEL) {
          onCancel?.();
        }
      })
      .setTitle('Select files from Google Drive')
      .setSelectableMimeTypes('application/pdf,application/vnd.google-apps.document,application/vnd.google-apps.spreadsheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .build();

    picker.setVisible(true);
  }

  async downloadFile(fileId: string): Promise<ArrayBuffer> {
    if (!this.oauthToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      {
        headers: {
          'Authorization': `Bearer ${this.oauthToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    return response.arrayBuffer();
  }

  isUserAuthenticated(): boolean {
    return !!this.oauthToken;
  }

  getAuthStatus(): { isInitialized: boolean; isAuthenticated: boolean } {
    return {
      isInitialized: this.isInitialized,
      isAuthenticated: !!this.oauthToken,
    };
  }
}

export const googlePickerService = new GooglePickerService();
